import React, { useState } from 'react';
import AssignmentForm from './components/AssignmentForm';
import ResultDisplay from './components/ResultDisplay';

function App() {
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState({ step: 0, total: 3 });

  const handleGenerateAssignment = async (formData) => {
    setLoading(true);
    setError('');
    setResult('');
    setProcessing({ step: 1, total: 3 });
    
    // Validate formData to ensure no undefined values
    if (!formData.writingSample || !formData.assignmentPrompt) {
      setError('Please provide both a writing sample and assignment prompt');
      setLoading(false);
      return;
    }
    
    // Set empty string for undefined values
    const sanitizedData = {
      writingSample: formData.writingSample || '',
      assignmentPrompt: formData.assignmentPrompt || '',
      materials: formData.materials || ''
    };
    
    try {
      setProcessing({ step: 1, total: 3, message: 'Analyzing writing style...' });
      
      // Set a longer timeout for the complex model (will likely exceed Vercel's limit)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
      
      const response = await fetch('/api/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sanitizedData),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      setProcessing({ step: 3, total: 3, message: 'Formatting results...' });
      
      // Check if the response is JSON before trying to parse it
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // If not JSON, get text and create our own error object
        const text = await response.text();
        throw new Error(`Server returned non-JSON response: ${text.substring(0, 100)}...`);
      }
      
      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }
      
      if (!data.text && !data.generatedText) {
        throw new Error('No text was generated. Please try again with different inputs.');
      }
      
      setResult(data.text || data.generatedText || '');
    } catch (err) {
      console.error('Error:', err);
      
      // Check for timeout specifically
      if (err.name === 'AbortError') {
        setError('Request timed out. The AI analysis is too complex for the current server configuration. Try with a smaller writing sample or contact the administrator to upgrade the server.');
      } else if (err.message.includes('FUNCTION_INVOCATION_TIMEOUT') || 
          err.message.includes('504')) {
        setError('The AI generation timed out. This happens because serverless functions have a time limit. Please try with a smaller writing sample or contact the administrator.');
      } else if (err.message.includes('API key')) {
        setError('Server configuration error with the AI service. Please contact the site administrator.');
      } else {
        setError(`Error: ${err.message}`);
      }
    } finally {
      setLoading(false);
      setProcessing({ step: 0, total: 3 });
    }
  };

  return (
    <div className="container">
      <header>
        <h1>AssignMate</h1>
        <p>Generate assignment responses in your own writing style</p>
        <p className="api-info">(Powered by Mistral AI)</p>
      </header>
      
      <main>
        <AssignmentForm 
          onSubmit={handleGenerateAssignment} 
          loading={loading} 
        />
        
        {loading && (
          <div className="card info">
            <p><strong>Processing:</strong> {processing.message || `Step ${processing.step} of ${processing.total}`}</p>
            <div className="progress-bar">
              <div 
                className="progress" 
                style={{width: `${(processing.step / processing.total) * 100}%`}}
              ></div>
            </div>
            <p className="small-text">Complex writing analysis in progress. This may take some time.</p>
          </div>
        )}
        
        {error && (
          <div className="card error">
            <p><strong>Error:</strong> {error}</p>
            <p>Note: The advanced writing analysis uses a complex AI model that may exceed server time limits.</p>
          </div>
        )}
        
        {result && !loading && (
          <ResultDisplay result={result} />
        )}
      </main>
      
      <footer>
        <p>&copy; {new Date().getFullYear()} AssignMate - All rights reserved</p>
      </footer>
    </div>
  );
}

export default App; 